import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Popover
} from '@mui/material';
import {
  Send as SendIcon,
  GroupAdd as GroupAddIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as AttachIcon,
  Sms as SmsIcon,
  AutoAwesome as AutoAwesomeIcon,
  InsertLink as InsertLinkIcon
} from '@mui/icons-material';
import axios from 'axios';

interface MessageComposerProps {
  twilioNumber?: string;
  onMessageSent?: () => void;
  defaultRecipient?: string;
}

interface Contact {
  id: string;
  phoneNumber: string;
  name: string;
  email?: string;
  note?: string;
}

interface Group {
  id: string;
  name: string;
  memberCount: number;
}

interface MessageTemplate {
  id: string;
  name: string;
  body: string;
}

type RecipientType = 'phone' | 'contact' | 'group';

const EMOJI_CATEGORIES: { label: string; emojis: string[] }[] = [
  { label: 'Smileys', emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😗','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','😐','😑','😶','😏','😒','🙄','😬','😮‍💨','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐'] },
  { label: 'Gestures', emojis: ['👋','🤚','🖐️','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏'] },
  { label: 'Hearts', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','♥️','🫶'] },
  { label: 'Objects', emojis: ['📱','💻','⌨️','🖥️','📞','☎️','📧','📨','✉️','📩','📝','📄','📋','📌','📎','🔗','✂️','📐','📏','🗂️','📁','📂','🗓️','📅','📆','🔔','🔕','📣','📢'] },
  { label: 'Symbols', emojis: ['✅','❌','⭕','🚫','💯','🔥','⚡','💥','✨','🌟','⭐','🎉','🎊','🏆','🥇','🎯','💡','📊','📈','📉','💰','💵','💸','🏷️','🔑','🔒','🔓'] },
  { label: 'Business', emojis: ['🤝','💼','📊','📈','🏢','🏗️','🛒','🛍️','💳','🧾','📦','🚚','✈️','🌍','🌎','🌏','⏰','⏳','📅','🗓️','📌','📍','🎯','💡','🔔','📣','📢','📬'] },
];

const MessageComposer: React.FC<MessageComposerProps> = ({ twilioNumber, onMessageSent, defaultRecipient }) => {
  const [recipientType, setRecipientType] = useState<RecipientType>(defaultRecipient ? 'phone' : 'phone');
  const [to, setTo] = useState(defaultRecipient || '');
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingRecipientCount, setPendingRecipientCount] = useState(0);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [scheduleForLater, setScheduleForLater] = useState(false);
  const [complianceFooter, setComplianceFooter] = useState(true);

  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const [emojiCategory, setEmojiCategory] = useState(0);

  // Link insert state
  const [linkAnchorEl, setLinkAnchorEl] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');

  // SMS info popover state
  const [smsInfoAnchorEl, setSmsInfoAnchorEl] = useState<HTMLElement | null>(null);

  const messageInputRef = useRef<HTMLInputElement>(null);

  const characterCount = message.length;
  const segmentCount = Math.ceil(characterCount / 160) || 1;

  useEffect(() => {
    if (recipientType === 'contact' && contacts.length === 0) fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientType]);

  useEffect(() => {
    if (recipientType === 'group' && groups.length === 0) fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientType]);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get('/api/templates');
      setTemplates(response.data.templates || []);
    } catch (err) { console.error('Error fetching templates:', err); }
  };

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await axios.get('/api/contacts');
      setContacts(response.data.contacts || []);
    } catch (err) { setError('Failed to load contacts'); }
    finally { setLoadingContacts(false); }
  };

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);
      const response = await axios.get('/api/groups');
      setGroups(response.data.groups || []);
    } catch (err) { setError('Failed to load groups'); }
    finally { setLoadingGroups(false); }
  };

  const insertTextAtCursor = (text: string) => {
    setMessage((prev) => prev + text);
  };

  const handleEmojiClick = (emoji: string) => {
    insertTextAtCursor(emoji);
  };

  const handleInsertLink = () => {
    if (linkUrl.trim()) {
      const url = linkUrl.trim().startsWith('http') ? linkUrl.trim() : `https://${linkUrl.trim()}`;
      insertTextAtCursor(url);
      setLinkUrl('');
      setLinkAnchorEl(null);
    }
  };

  const handleSend = async () => {
    if (recipientType === 'phone') {
      if (!to.trim()) { setError('Please enter a recipient phone number'); return; }
      const numbers = to.split(',').map(n => n.trim().replace(/[\s\-()]/g, '')).filter(n => n);
      if (numbers.length === 0) { setError('Please enter a recipient phone number'); return; }
      const phoneRegex = /^\+?[1-9]\d{1,14}$/;
      for (const num of numbers) {
        if (!phoneRegex.test(num)) { setError(`Invalid phone number: ${num}`); return; }
      }
    } else if (recipientType === 'contact') {
      if (selectedContacts.length === 0) { setError('Please select at least one contact'); return; }
    } else if (recipientType === 'group') {
      if (selectedGroups.length === 0) { setError('Please select at least one group'); return; }
      const totalMembers = selectedGroups.reduce((sum, g) => sum + g.memberCount, 0);
      if (totalMembers === 0) { setError('Selected groups have no members'); return; }
      setPendingRecipientCount(totalMembers);
      setConfirmDialogOpen(true);
      return;
    }
    if (!message.trim()) { setError('Please enter a message'); return; }
    await sendMessage();
  };

  const handleConfirmGroupSend = async () => { setConfirmDialogOpen(false); await sendMessage(); };
  const handleCancelGroupSend = () => { setConfirmDialogOpen(false); };

  const sendMessage = async () => {
    try {
      setSending(true); setError(''); setSuccess('');

      if (recipientType === 'phone') {
        const allNumbers = to.split(',').map(n => n.trim().replace(/[\s\-()]/g, '')).filter(n => n);
        const numbers = allNumbers.filter((n, i) => allNumbers.indexOf(n) === i); // deduplicate
        let sentCount = 0;
        for (const phone of numbers) {
          await axios.post('/api/messages/send', { to: phone, body: message });
          sentCount++;
        }
        setSuccess(sentCount > 1 ? `Message sent to ${sentCount} recipients!` : 'Message sent successfully!');
      } else if (recipientType === 'contact') {
        // Deduplicate by phone number
        const uniquePhones: string[] = [];
        const seen = new Set<string>();
        selectedContacts.forEach(c => {
          if (!seen.has(c.phoneNumber)) {
            seen.add(c.phoneNumber);
            uniquePhones.push(c.phoneNumber);
          }
        });
        let sentCount = 0;
        for (const phone of uniquePhones) {
          await axios.post('/api/messages/send', { to: phone, body: message });
          sentCount++;
        }
        setSuccess(`Message sent to ${sentCount} contact${sentCount !== 1 ? 's' : ''}!`);
      } else if (recipientType === 'group') {
        // Send to each group; backend handles per-group dedup, but we track total
        let totalSent = 0;
        const sentPhones = new Set<string>();
        for (const group of selectedGroups) {
          try {
            const res = await axios.post('/api/messages/send-to-group', { groupId: group.id, body: message, excludePhones: Array.from(sentPhones) });
            const count = res.data.sentCount || group.memberCount;
            totalSent += count;
            // Track sent phones for dedup across groups
            if (res.data.sentPhones) {
              res.data.sentPhones.forEach((p: string) => sentPhones.add(p));
            }
          } catch (err: any) {
            // Continue sending to other groups even if one fails
            console.error(`Failed to send to group ${group.name}:`, err);
          }
        }
        setSuccess(`Message sent to ${totalSent} recipient${totalSent !== 1 ? 's' : ''}!`);
      }

      setMessage(''); setTo(''); setSelectedContacts([]); setSelectedGroups([]); setSelectedTemplate(null);
      if (onMessageSent) onMessageSent();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send message.');
    } finally { setSending(false); }
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3436', mb: 0.5 }}>New Message</Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Compose and broadcast a new SMS campaign or individual message.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || !message.trim()}
            sx={{
              bgcolor: '#6c5ce7',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              py: 1.2,
              '&:hover': { bgcolor: '#5a4bd1' },
              '&.Mui-disabled': { bgcolor: '#ccc' },
            }}
          >
            Send Message
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {/* Form Card */}
        <Paper sx={{ borderRadius: 3, p: { xs: 2, md: 3 }, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          {/* Recipient */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
            Recipient(s)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {recipientType === 'phone' && (
              <TextField
                fullWidth
                size="small"
                placeholder="Enter phone numbers (comma-separated for multiple)..."
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={sending}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa', '& fieldset': { borderColor: '#e8e8ef' } } }}
                helperText={to.includes(',') ? `${to.split(',').filter(n => n.trim()).length} recipients` : undefined}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setRecipientType('group')} sx={{ color: '#6c5ce7' }}>
                        <GroupAddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {recipientType === 'contact' && (
              <Autocomplete
                multiple
                fullWidth
                options={contacts}
                getOptionLabel={(option) => `${option.name} (${option.phoneNumber})`}
                value={selectedContacts}
                onChange={(_, v) => setSelectedContacts(v)}
                loading={loadingContacts}
                disabled={sending}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} placeholder={selectedContacts.length === 0 ? "Search contacts..." : ""} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
                )}
              />
            )}
            {recipientType === 'group' && (
              <Autocomplete
                multiple
                fullWidth
                options={groups}
                getOptionLabel={(option) => `${option.name} (${option.memberCount} members)`}
                value={selectedGroups}
                onChange={(_, v) => setSelectedGroups(v)}
                loading={loadingGroups}
                disabled={sending}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} placeholder={selectedGroups.length === 0 ? "Search groups..." : ""} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
                )}
              />
            )}
          </Box>

          {/* Recipient type quick switch */}
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {(['phone', 'contact', 'group'] as RecipientType[]).map((type) => (
              <Chip
                key={type}
                label={type === 'phone' ? 'Phone' : type === 'contact' ? 'Contact' : 'Group'}
                size="small"
                onClick={() => { setRecipientType(type); setTo(''); setSelectedContacts([]); setSelectedGroups([]); }}
                sx={{
                  bgcolor: recipientType === type ? '#f0edff' : '#f5f5f5',
                  color: recipientType === type ? '#6c5ce7' : '#888',
                  fontWeight: recipientType === type ? 600 : 400,
                  border: recipientType === type ? '1px solid #6c5ce7' : '1px solid transparent',
                  cursor: 'pointer',
                }}
              />
            ))}
          </Box>

          {/* Sender ID */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
              Sender ID
            </Typography>
            <Select
              fullWidth
              size="small"
              value={twilioNumber || 'default'}
              disabled
              sx={{ borderRadius: 2, bgcolor: '#fafafa', '& fieldset': { borderColor: '#e8e8ef' } }}
            >
              <MenuItem value={twilioNumber || 'default'}>
                {twilioNumber || 'ConnectPie SMS (Local)'}
              </MenuItem>
            </Select>
          </Box>

          {/* Message Content */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Message Content
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
              onClick={() => {
                if (templates.length > 0 && !selectedTemplate) {
                  setSelectedTemplate(templates[0]);
                  setMessage(templates[0].body);
                }
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 14, color: '#6c5ce7' }} />
              <Typography variant="caption" sx={{ color: '#6c5ce7', fontWeight: 500 }}>Use Template</Typography>
            </Box>
          </Box>

          {/* Template selector */}
          {templates.length > 0 && (
            <Autocomplete
              options={templates}
              getOptionLabel={(option) => option.name}
              value={selectedTemplate}
              onChange={(_, v) => { setSelectedTemplate(v); if (v) setMessage(v.body); }}
              disabled={sending}
              size="small"
              sx={{ mb: 1.5 }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select a template..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }} />
              )}
            />
          )}

          <Box sx={{ border: '1px solid #e8e8ef', borderRadius: 2, overflow: 'hidden', bgcolor: '#fafafa' }}>
            <TextField
              fullWidth
              multiline
              rows={5}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={sending}
              variant="standard"
              inputRef={messageInputRef}
              sx={{
                px: 2, pt: 1.5,
                '& .MuiInput-underline:before': { display: 'none' },
                '& .MuiInput-underline:after': { display: 'none' },
              }}
              inputProps={{ maxLength: 1600 }}
            />
            {/* Bottom toolbar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid #f0f0f0' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton size="small" sx={{ color: '#999', '&:hover': { color: '#6c5ce7' } }} onClick={(e) => setEmojiAnchorEl(e.currentTarget)} title="Insert emoji">
                  <EmojiIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#999', '&:hover': { color: '#6c5ce7' } }} onClick={(e) => setLinkAnchorEl(e.currentTarget)} title="Insert link">
                  <AttachIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#999', '&:hover': { color: '#6c5ce7' } }} onClick={(e) => setSmsInfoAnchorEl(e.currentTarget)} title="SMS segment info">
                  <SmsIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  {characterCount} / 160 characters
                </Typography>
                <Chip
                  label={`${segmentCount} Segment${segmentCount !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: '#f0edff', color: '#6c5ce7', fontWeight: 500, fontSize: 11, height: 22 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Options */}
        </Paper>
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={Boolean(emojiAnchorEl)}
        anchorEl={emojiAnchorEl}
        onClose={() => setEmojiAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ width: 320, maxHeight: 360, p: 1.5 }}>
          {/* Category tabs */}
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, overflowX: 'auto', pb: 0.5 }}>
            {EMOJI_CATEGORIES.map((cat, idx) => (
              <Chip
                key={cat.label}
                label={cat.label}
                size="small"
                onClick={() => setEmojiCategory(idx)}
                sx={{
                  fontSize: 11,
                  height: 24,
                  bgcolor: emojiCategory === idx ? '#f0edff' : '#f5f5f5',
                  color: emojiCategory === idx ? '#6c5ce7' : '#666',
                  fontWeight: emojiCategory === idx ? 600 : 400,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
          {/* Emoji grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.25, maxHeight: 260, overflowY: 'auto' }}>
            {EMOJI_CATEGORIES[emojiCategory].emojis.map((emoji) => (
              <Box
                key={emoji}
                onClick={() => handleEmojiClick(emoji)}
                sx={{
                  fontSize: 22,
                  width: 36,
                  height: 36,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#f0edff' },
                }}
              >
                {emoji}
              </Box>
            ))}
          </Box>
        </Box>
      </Popover>

      {/* Insert Link Popover */}
      <Popover
        open={Boolean(linkAnchorEl)}
        anchorEl={linkAnchorEl}
        onClose={() => { setLinkAnchorEl(null); setLinkUrl(''); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#2d3436' }}>Insert Link</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="https://example.com"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleInsertLink(); }}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><InsertLinkIcon sx={{ color: '#999', fontSize: 18 }} /></InputAdornment>,
            }}
            autoFocus
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => { setLinkAnchorEl(null); setLinkUrl(''); }} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleInsertLink} disabled={!linkUrl.trim()} sx={{ textTransform: 'none', bgcolor: '#6c5ce7', '&:hover': { bgcolor: '#5a4bd1' } }}>Insert</Button>
          </Box>
        </Box>
      </Popover>

      {/* SMS Info Popover */}
      <Popover
        open={Boolean(smsInfoAnchorEl)}
        anchorEl={smsInfoAnchorEl}
        onClose={() => setSmsInfoAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2.5, width: 280 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#2d3436' }}>SMS Segment Info</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Characters</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436' }}>{characterCount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Segments</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#6c5ce7' }}>{segmentCount}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Encoding</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436' }}>{characterCount > 0 && /[^\x00-\x7F]/.test(message) ? 'Unicode (70/seg)' : 'GSM-7 (160/seg)'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>Remaining</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436' }}>{(segmentCount * 160) - characterCount} chars</Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f8f7ff', borderRadius: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6c5ce7', lineHeight: 1.4 }}>
              SMS messages over 160 characters are split into multiple segments. Each segment is billed separately. Unicode characters (emojis) reduce the limit to 70 characters per segment.
            </Typography>
          </Box>
        </Box>
      </Popover>

      {/* Group Send Confirmation */}
      <Dialog open={confirmDialogOpen} onClose={handleCancelGroupSend}>
        <DialogTitle>Confirm Group Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to send this message to approximately {pendingRecipientCount} recipient{pendingRecipientCount !== 1 ? 's' : ''} across {selectedGroups.length} group{selectedGroups.length !== 1 ? 's' : ''}. Duplicate contacts across groups will only receive the message once. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelGroupSend} color="inherit">Cancel</Button>
          <Button onClick={handleConfirmGroupSend} variant="contained" sx={{ bgcolor: '#6c5ce7', '&:hover': { bgcolor: '#5a4bd1' } }}>
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessageComposer;
